import { Accounts } from "@/modules/account/components";
import { Bonds } from "@/modules/bond/components";
import { Cards } from "@/modules/card/components";
import { MyFinanceTabs } from "@/modules/dashboard/common/enum";
import { getMyFinanceTabItems } from "@/modules/dashboard/helpers";
import { Deposits } from "@/modules/deposit/components";
import { LoanType } from "@/modules/loan/common/enum";
import { Loans } from "@/modules/loan/components";
import { Stocks } from "@/modules/stock/components";
import { AppPaths } from "@/shared/common/enums";
import { ScrollableTab, SeeMore } from "@/shared/components";
import { buildRoute } from "@/shared/helpers";
import { Heading } from "@ab.uitools/ui-library/components/Heading";
import { type ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

type ITemplate = {
  [key in MyFinanceTabs]: ReactNode;
};

const ITEMS_LIMIT = 5;

export const MyFinances = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<string>(MyFinanceTabs.Accounts);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  const template: ITemplate = {
    [MyFinanceTabs.Accounts]: <Accounts limit={ITEMS_LIMIT}/>,
    [MyFinanceTabs.Cards]: <Cards limit={ITEMS_LIMIT}/>,
    [MyFinanceTabs.Deposits]: <Deposits limit={ITEMS_LIMIT}/>,
    [MyFinanceTabs.Loans]: <Loans limit={ITEMS_LIMIT}/>,
    [MyFinanceTabs.Stocks]: <Stocks limit={ITEMS_LIMIT}/>,
    [MyFinanceTabs.Bonds]: <Bonds limit={ITEMS_LIMIT}/>,
  };

  const routes = {
    [MyFinanceTabs.Accounts]: AppPaths.Accounts,
    [MyFinanceTabs.Cards]: AppPaths.Cards,
    [MyFinanceTabs.Deposits]: AppPaths.Deposits,
    [MyFinanceTabs.Loans]: buildRoute(AppPaths.Loans, {
      type: LoanType.Standard
    }),
    [MyFinanceTabs.Stocks]: AppPaths.Stocks,
  };

  const handleSeeMore = () => {
    navigate(routes[selectedTab as keyof typeof routes]);
  };

  return (
    <div className="card my-finances" ref={sectionRef}>
      <div className="flexbox align-items--center">
        <Heading className="content-title" weight="bold" size="xsmall">
          {t("dashboard.myFinances.title")}
        </Heading>
        <SeeMore action={handleSeeMore} className="ml-8"/>
      </div>
      <ScrollableTab
        tabEnum={MyFinanceTabs}
        className="mt-16"
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        getTabItems={getMyFinanceTabItems}
      />
      {template[selectedTab as MyFinanceTabs]}
    </div>
  );
};
